import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// bskr/sticker
//
// Frontend: drop + impact + wobble GSAP timeline ported from panel-text-block.
// Editor:   drag the sticker to reposition. On release the closest of nine
//           desktop anchors is auto-selected and ACF's data attribute is
//           updated via wp.data.dispatch, triggering the block to re-render
//           server-side with the new desktop_alignment + offset_x/y.
//
// One script handles both contexts; the editor branch only runs when
// window.wp.data is available, which is only true inside Gutenberg.

const SNAP_PX = 5;
const ANCHOR_SNAP_PX = 15; // distance from an anchor reference point that triggers magnetic snap
const DRAG_THRESHOLD = 3;

// Each of the nine anchors maps a reference point on the group (gx, gy in
// 0..1) to the matching reference point on the sticker (sx, sy as edge name).
// The CSS for each anchor pins the sticker's matching edge/center to the
// group's same edge/center; auto-detection finds the anchor whose pair of
// reference points has the smallest residual offset.
const ANCHORS = [
  { key: "top-left",      gx: 0,   gy: 0,   sx: "left",   sy: "top"    },
  { key: "top-center",    gx: 0.5, gy: 0,   sx: "center", sy: "top"    },
  { key: "top-right",     gx: 1,   gy: 0,   sx: "right",  sy: "top"    },
  { key: "center-left",   gx: 0,   gy: 0.5, sx: "left",   sy: "center" },
  { key: "center",        gx: 0.5, gy: 0.5, sx: "center", sy: "center" },
  { key: "center-right",  gx: 1,   gy: 0.5, sx: "right",  sy: "center" },
  { key: "bottom-left",   gx: 0,   gy: 1,   sx: "left",   sy: "bottom" },
  { key: "bottom-center", gx: 0.5, gy: 1,   sx: "center", sy: "bottom" },
  { key: "bottom-right",  gx: 1,   gy: 1,   sx: "right",  sy: "bottom" },
];

// Editor-drag state. Declared up here (before start()) because const bindings
// hit a TDZ if referenced before their declaration line — function decls are
// hoisted, but the WeakSet/let they capture in their closures aren't.
const attachedDocs = new WeakSet();
let dragState = null;

function start() {
  if (typeof window !== "undefined" && window.wp && window.wp.data) {
    initEditorDrag();
  } else {
    initFrontendAnimation();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}

// ---------- Frontend animation ----------

function initFrontendAnimation() {
  const stickers = document.querySelectorAll(".bskr-sticker");
  if (!stickers.length) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  stickers.forEach((sticker) => {
    const image = sticker.querySelector(".bskr-sticker__image");
    if (!image) return;
    const img = image.querySelector("img");
    if (!img) return;

    const rotationVar = getComputedStyle(sticker)
      .getPropertyValue("--bskr-sticker-rotation")
      .trim();
    const finalRotation = parseFloat(rotationVar) || 0;

    if (reduceMotion) {
      gsap.set(img, { autoAlpha: 1, scale: 1, rotation: finalRotation, yPercent: 0 });
      return;
    }

    gsap.set(img, {
      autoAlpha: 0,
      scale: 1.7,
      rotation: finalRotation + 35,
      yPercent: -60,
      transformOrigin: "50% 50%",
    });

    gsap
      .timeline({
        scrollTrigger: { trigger: sticker, start: "top 80%", once: true },
      })
      .to(img, {
        autoAlpha: 1,
        scale: 1.05,
        rotation: finalRotation - 4,
        yPercent: 0,
        duration: 0.45,
        ease: "power2.in",
      })
      .to(img, { scale: 0.94, duration: 0.12, ease: "power2.out" })
      .to(img, {
        scale: 1,
        rotation: finalRotation,
        duration: 0.5,
        ease: "elastic.out(1, 0.3)",
        // Clear only the properties GSAP introduced. Using `all` was also
        // wiping inline `width`/`height` that Gutenberg sets on the <img>
        // when the user resizes the image in the admin (especially relevant
        // for SVGs, which lose intrinsic sizing once their inline dimensions
        // are stripped and snap back to their viewBox defaults).
        clearProps: "transform,opacity,visibility,transform-origin",
      });
  });
}

// ---------- Editor drag ----------

function initEditorDrag() {
  scanAndAttach();
  // Gutenberg lazy-mounts the iframed canvas, and ACF re-renders blocks by
  // replacing their inner HTML — keep re-scanning so we catch newly inserted
  // stickers and iframe canvases.
  new MutationObserver(scanAndAttach).observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function scanAndAttach() {
  attachToDoc(document);
  // Scan every same-origin iframe (modern Gutenberg uses an iframed canvas,
  // typically name="editor-canvas", but it's safer not to rely on the name).
  document.querySelectorAll("iframe").forEach((iframe) => {
    let doc;
    try {
      doc = iframe.contentDocument;
    } catch (_) {
      return; // cross-origin
    }
    if (doc) attachToDoc(doc);
    if (!iframe.dataset.bskrStickerHooked) {
      iframe.dataset.bskrStickerHooked = "1";
      iframe.addEventListener("load", () => {
        try {
          attachedDocs.delete(iframe.contentDocument);
        } catch (_) {}
        scanAndAttach();
      });
    }
  });
}

function attachToDoc(doc) {
  if (!doc || !doc.body) return;
  if (!attachedDocs.has(doc)) {
    attachedDocs.add(doc);
    doc.addEventListener("pointerdown", onPointerDown, true);
    new MutationObserver(() => markEditorStickers(doc)).observe(doc.body, {
      childList: true,
      subtree: true,
    });
  }
  markEditorStickers(doc);
}

function markEditorStickers(doc) {
  doc.querySelectorAll(".bskr-sticker").forEach((s) => {
    s.classList.add("bskr-sticker--editor");
  });
}

function onPointerDown(e) {
  if (e.button !== 0) return;
  const image = e.target.closest(".bskr-sticker__image");
  if (!image) return;
  const group = image.closest(".bskr-sticker__group");
  if (!group) return;
  if (group.childElementCount <= 1) return; // sticker is :only-child → in flow, no drag.
  const outer = image.closest(".bskr-sticker");
  if (!outer) return;
  // Walk up to the bskr/sticker block's own Gutenberg wrapper — `[data-block]`
  // alone would land on the locked core/image's wrapper (the closest block),
  // and we'd end up dispatching ACF data updates against the wrong clientId.
  const blockEl = image.closest('[data-type="bskr/sticker"]');
  const clientId = blockEl ? blockEl.getAttribute("data-block") : null;
  if (!clientId) return;

  const groupRect = group.getBoundingClientRect();
  const stickerRect = image.getBoundingClientRect();

  dragState = {
    doc: image.ownerDocument,
    image,
    group,
    outer,
    clientId,
    startStickerLeft: stickerRect.left - groupRect.left,
    startStickerTop: stickerRect.top - groupRect.top,
    stickerWidth: stickerRect.width,
    stickerHeight: stickerRect.height,
    groupWidth: groupRect.width,
    groupHeight: groupRect.height,
    startMouseX: e.clientX,
    startMouseY: e.clientY,
    dragging: false,
  };

  dragState.doc.addEventListener("pointermove", onPointerMove, true);
  dragState.doc.addEventListener("pointerup", onPointerUp, true);
}

function onPointerMove(e) {
  if (!dragState) return;
  const dx = e.clientX - dragState.startMouseX;
  const dy = e.clientY - dragState.startMouseY;

  if (!dragState.dragging) {
    if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
    dragState.dragging = true;
    dragState.outer.classList.add("bskr-sticker--dragging");
    applyInlineDrag(
      dragState.image,
      dragState.startStickerLeft,
      dragState.startStickerTop
    );
  }

  e.preventDefault();
  e.stopPropagation();

  let newLeft = dragState.startStickerLeft + dx;
  let newTop = dragState.startStickerTop + dy;
  if (!e.shiftKey) {
    // Magnetic snap to the nearest of the nine anchor points: as soon as the
    // sticker's matching reference point gets within ANCHOR_SNAP_PX of the
    // group's, lock the sticker onto that anchor exactly. Falls back to a
    // 5px grid otherwise so positions stay tidy.
    const snap = findAnchorSnap(
      newLeft,
      newTop,
      dragState.stickerWidth,
      dragState.stickerHeight,
      dragState.groupWidth,
      dragState.groupHeight
    );
    if (snap) {
      newLeft = snap.left;
      newTop = snap.top;
    } else {
      newLeft = Math.round(newLeft / SNAP_PX) * SNAP_PX;
      newTop = Math.round(newTop / SNAP_PX) * SNAP_PX;
    }
  }
  applyInlineDrag(dragState.image, newLeft, newTop);
}

function findAnchorSnap(left, top, sW, sH, gW, gH) {
  // Two snap modes per anchor:
  //   1. edge-snap: the sticker's matching corner/edge sits exactly on the
  //      group's reference point — sticker fully inside, hugging that anchor.
  //   2. center-snap: the sticker's center sits exactly on the group's
  //      reference point — sticker straddles the anchor (half outside on
  //      whichever sides apply).
  // Both modes contribute candidates; the geometrically closest wins.
  let best = null;
  const sCenterX = left + sW / 2;
  const sCenterY = top + sH / 2;

  const consider = (gx, gy, sX, sY, anchorKey) => {
    const dx = gx - sX;
    const dy = gy - sY;
    const dist = Math.hypot(dx, dy);
    if (dist < ANCHOR_SNAP_PX && (!best || dist < best.dist)) {
      best = { left: left + dx, top: top + dy, dist, anchor: anchorKey };
    }
  };

  for (const a of ANCHORS) {
    const gPointX = a.gx * gW;
    const gPointY = a.gy * gH;

    const sEdgeX =
      a.sx === "left" ? left : a.sx === "right" ? left + sW : sCenterX;
    const sEdgeY =
      a.sy === "top" ? top : a.sy === "bottom" ? top + sH : sCenterY;

    consider(gPointX, gPointY, sEdgeX, sEdgeY, a.key);   // edge-snap
    consider(gPointX, gPointY, sCenterX, sCenterY, a.key); // center-snap
  }
  return best;
}

function applyInlineDrag(image, left, top) {
  image.style.position = "absolute";
  image.style.top = `${top}px`;
  image.style.left = `${left}px`;
  image.style.right = "auto";
  image.style.bottom = "auto";
  image.style.transform = "none";
}

function onPointerUp(e) {
  if (!dragState) return;
  const state = dragState;
  dragState = null;

  state.doc.removeEventListener("pointermove", onPointerMove, true);
  state.doc.removeEventListener("pointerup", onPointerUp, true);

  if (!state.dragging) return;

  e.preventDefault();
  e.stopPropagation();

  const groupRect = state.group.getBoundingClientRect();
  const stickerRect = state.image.getBoundingClientRect();
  const sLeft = stickerRect.left - groupRect.left;
  const sTop = stickerRect.top - groupRect.top;
  const sRight = groupRect.right - stickerRect.right;
  const sBottom = groupRect.bottom - stickerRect.bottom;
  const sCenterX = sLeft + stickerRect.width / 2;
  const sCenterY = sTop + stickerRect.height / 2;
  const gCenterX = groupRect.width / 2;
  const gCenterY = groupRect.height / 2;

  let best = null;
  for (const a of ANCHORS) {
    const x =
      a.sx === "left" ? sLeft : a.sx === "right" ? sRight : sCenterX - gCenterX;
    const y =
      a.sy === "top" ? sTop : a.sy === "bottom" ? sBottom : sCenterY - gCenterY;
    const dist = Math.hypot(x, y);
    if (!best || dist < best.dist) best = { anchor: a.key, x, y, dist };
  }

  const snap = !e.shiftKey;
  const finalX = snap
    ? Math.round(best.x / SNAP_PX) * SNAP_PX
    : Math.round(best.x);
  const finalY = snap
    ? Math.round(best.y / SNAP_PX) * SNAP_PX
    : Math.round(best.y);

  try {
    const block = window.wp.data
      .select("core/block-editor")
      .getBlock(state.clientId);
    const currentData = (block && block.attributes && block.attributes.data) || {};
    window.wp.data
      .dispatch("core/block-editor")
      .updateBlockAttributes(state.clientId, {
        data: {
          ...currentData,
          desktop_alignment: best.anchor,
          desktop_offset_x: finalX,
          desktop_offset_y: finalY,
        },
      });
    // ACF re-renders the block: its inner HTML is replaced, so our inline
    // drag styles and --dragging class disappear with the old DOM.
  } catch (_) {
    // If dispatch fails, restore the visual state so the sticker isn't stuck
    // mid-drag.
    clearInlineDrag(state.image);
    state.outer.classList.remove("bskr-sticker--dragging");
  }
}

function clearInlineDrag(image) {
  image.style.position = "";
  image.style.top = "";
  image.style.left = "";
  image.style.right = "";
  image.style.bottom = "";
  image.style.transform = "";
}
